import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { apiService } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  Users, 
  Building2, 
  Calendar, 
  Flame,
  TrendingUp,
  Activity,
  Shield,
  User,
  Mail,
  ToggleLeft,
  Crown,
  Loader2,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { toast } from 'sonner';

export const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const { user, isAdminMaster } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!isAdminMaster) {
      navigate('/');
      return;
    }
    loadData();
  }, [isAdminMaster, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [dashboardData, usersData] = await Promise.all([
        apiService.getAdminDashboard(),
        apiService.getAdminUsers()
      ]);
      setStats(dashboardData);
      setUsuarios(usersData);
    } catch (error) {
      console.error('Failed to load admin data:', error);
      toast.error('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
    toast.success('Dados atualizados!');
  };

  const handleToggleActive = async (userId) => {
    try {
      const result = await apiService.toggleUserActive(userId);
      setUsuarios(prev => 
        prev.map(u => u.id === userId ? { ...u, ativo: result.ativo } : u)
      );
      toast.success(result.message);
    } catch (error) {
      toast.error('Erro ao alterar status do usuário');
    }
  };

  const handleToggleRole = async (userId, currentRole) => {
    try {
      const newRole = currentRole === 'admin_master' ? 'user' : 'admin_master';
      const result = await apiService.updateUserRole(userId, newRole);
      setUsuarios(prev => 
        prev.map(u => u.id === userId ? { ...u, role: result.role } : u)
      );
      toast.success(result.message);
    } catch (error) {
      toast.error('Erro ao alterar papel do usuário');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const statCards = [
    { 
      label: 'Total de Usuários', 
      value: stats?.total_usuarios || 0, 
      icon: Users, 
      color: 'bg-blue-500',
      trend: '+12%',
      trendUp: true
    },
    { 
      label: 'Igrejas Cadastradas', 
      value: stats?.total_igrejas || 0, 
      icon: Building2, 
      color: 'bg-purple-500',
      trend: '+5%',
      trendUp: true
    },
    { 
      label: 'Células Ativas', 
      value: stats?.total_celulas || 0, 
      icon: Flame, 
      color: 'bg-primary',
      trend: '+8%',
      trendUp: true
    },
    { 
      label: 'Encontros Realizados', 
      value: stats?.total_encontros || 0, 
      icon: Calendar, 
      color: 'bg-green-500',
      trend: '+15%',
      trendUp: true
    },
  ];

  return (
    <div className="page-transition">
      {/* Header */}
      <div className="px-6 lg:px-8 py-8 bg-gradient-to-r from-primary/10 via-purple-500/10 to-blue-500/10 border-b border-border">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-primary text-primary-foreground">
                <Shield className="w-8 h-8" />
              </div>
              <div>
                <h1 className="font-heading text-3xl lg:text-4xl font-bold text-foreground">
                  Dashboard Admin
                </h1>
                <p className="text-muted-foreground">
                  Visão geral do sistema • {user?.nome}
                </p>
              </div>
            </div>
            
            <Button onClick={handleRefresh} variant="outline" disabled={refreshing}>
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          </div>
        </div>
      </div>

      <div className="px-6 lg:px-8 py-8">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {statCards.map((stat, index) => (
              <Card key={index} className="card-elevated animate-scale-in" style={{ animationDelay: `${index * 100}ms` }}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className={`p-3 rounded-xl ${stat.color} text-white`}>
                      <stat.icon className="w-6 h-6" />
                    </div>
                    <div className={`flex items-center gap-1 text-sm ${stat.trendUp ? 'text-green-500' : 'text-red-500'}`}>
                      {stat.trendUp ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                      {stat.trend}
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-3xl font-bold text-foreground">{stat.value}</p>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Charts Section */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Activity Chart Placeholder */}
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-primary" />
                  Atividade do Sistema
                </CardTitle>
                <CardDescription>
                  Visão geral das atividades recentes
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64 flex items-center justify-center bg-muted/30 rounded-lg">
                  <div className="text-center">
                    <TrendingUp className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                    <p className="text-muted-foreground">Gráfico de atividades</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Sistema em crescimento constante
                    </p>
                  </div>
                </div>
                {/* Quick Stats */}
                <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-border">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-foreground">{stats?.total_usuarios || 0}</p>
                    <p className="text-xs text-muted-foreground">Usuários</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-foreground">{stats?.total_celulas || 0}</p>
                    <p className="text-xs text-muted-foreground">Células</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-foreground">{stats?.total_encontros || 0}</p>
                    <p className="text-xs text-muted-foreground">Encontros</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Distribution Chart Placeholder */}
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-purple-500" />
                  Distribuição
                </CardTitle>
                <CardDescription>
                  Distribuição de células por igreja
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64 flex items-center justify-center bg-muted/30 rounded-lg">
                  <div className="text-center">
                    <Flame className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                    <p className="text-muted-foreground">Gráfico de distribuição</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Células distribuídas em {stats?.total_igrejas || 0} igrejas
                    </p>
                  </div>
                </div>
                {/* Distribution Stats */}
                <div className="space-y-3 mt-4 pt-4 border-t border-border">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Média de células por igreja</span>
                    <span className="font-medium">
                      {stats?.total_igrejas > 0 
                        ? (stats.total_celulas / stats.total_igrejas).toFixed(1) 
                        : '0'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Média de encontros por célula</span>
                    <span className="font-medium">
                      {stats?.total_celulas > 0 
                        ? (stats.total_encontros / stats.total_celulas).toFixed(1) 
                        : '0'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Users Management */}
          <Card className="card-elevated">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-500" />
                    Gerenciamento de Usuários
                  </CardTitle>
                  <CardDescription>
                    {usuarios.length} usuários cadastrados
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {usuarios.map((usuario, index) => (
                  <div 
                    key={usuario.id} 
                    className="flex items-center justify-between p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors animate-slide-up"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <div className="flex items-center gap-4">
                      <Avatar className="h-12 w-12">
                        <AvatarFallback className="bg-primary/10 text-primary text-lg">
                          {usuario.nome?.charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-foreground">{usuario.nome}</p>
                          {usuario.role === 'admin_master' && (
                            <Badge className="bg-primary/10 text-primary border-primary/20">
                              <Crown className="w-3 h-3 mr-1" />
                              Admin Master
                            </Badge>
                          )}
                          {!usuario.ativo && (
                            <Badge variant="destructive">Inativo</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Mail className="w-3 h-3" />
                          {usuario.email}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {/* Toggle Active */}
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Ativo</span>
                        <Switch
                          checked={usuario.ativo}
                          onCheckedChange={() => handleToggleActive(usuario.id)}
                          disabled={usuario.id === user?.id}
                        />
                      </div>
                      
                      {/* Toggle Admin */}
                      {usuario.id !== user?.id && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleRole(usuario.id, usuario.role)}
                        >
                          {usuario.role === 'admin_master' ? 'Remover Admin' : 'Tornar Admin'}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                
                {usuarios.length === 0 && (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                    <p className="text-muted-foreground">Nenhum usuário encontrado</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
